import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, CircularProgress, TextField, InputAdornment, Autocomplete, Tabs, Tab } from "@mui/material";
import toast from "react-hot-toast";

import axiosConfig from "../../axios/axiosConfig";

import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import GroupsIcon from "@mui/icons-material/Groups";
import BarChartIcon from "@mui/icons-material/BarChart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";

// ── palette (brand) ──────────────────────────────────────────────────────
const pageBg = "#eef2f7";
const cardBg = "#ffffff";
const cardBorder = "#e2e8f0";
const navy = "#04343a";
const brandGreen = "#016551";
const negRed = "#dc2626";
const amber = "#b45309";
const textMain = "#1e293b";
const textMuted = "#64748b";

const TYPE_COLORS = {
  Advisory: "#6d28d9",
  Cash: "#0284c7",
  Future: "#b45309",
  Option: "#dc2626",
};

const num = (n) => (typeof n === "number" && !isNaN(n) ? n : Number(n) || 0);
const fmtInt = (n) => Math.round(num(n)).toLocaleString("en-IN");
const fmtPct = (n) => `${num(n).toFixed(2)}%`;
const fmtRs = (n) => `₹${fmtInt(num(n))}`;
const winRateColor = (n) => (num(n) >= 60 ? brandGreen : num(n) >= 40 ? amber : negRed);
const typeColor = (t) => TYPE_COLORS[t] || textMuted;

const StatCard = ({ icon, iconColor, value, label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", p: "16px 20px", flex: 1, minWidth: 200 }}>
    <Box sx={{ width: 48, height: 48, borderRadius: "50%", background: iconColor, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {React.cloneElement(icon, { sx: { color: "#fff", fontSize: 26 } })}
    </Box>
    <Box>
      <Typography sx={{ fontSize: 22, fontWeight: 700, color: textMain, lineHeight: 1.1, fontFamily: "monospace" }}>{value}</Typography>
      <Typography sx={{ fontSize: 12, color: textMuted, mt: "2px" }}>{label}</Typography>
    </Box>
  </Box>
);

const TypeChip = ({ type }) => (
  <Box
    component="span"
    sx={{
      display: "inline-block",
      fontSize: 11,
      fontWeight: 700,
      color: "#fff",
      background: typeColor(type),
      borderRadius: "999px",
      padding: "3px 10px",
      whiteSpace: "nowrap",
    }}
  >
    {type}
  </Box>
);

// ── trades normalization ─────────────────────────────────────────────────
const TRADE_GROUP_KEYS = { advisory: "Advisory", cash: "Cash", future: "Future", option: "Option" };

const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

const statusOf = (raw) => {
  const s = (raw || "").toUpperCase();
  return s === "CLOSE" ? "CLOSED" : s || "—";
};

const normalizeTrade = (type, t) => {
  let symbol, subtitle, entry, exit, target, stopLoss, signal, pnlPercent, date;

  if (type === "Advisory" || type === "Cash") {
    symbol = t.symbol_name;
    subtitle = t.segment;
    entry = toNum(t.entry_price);
    exit = toNum(t.exit_price);
    target = toNum(t.target_price);
    stopLoss = toNum(t.stop_loss);
    signal = t.signal || t.advisory_type;
    pnlPercent = toNum(t.profit_loss);
    date = t.entry_time || t.created_at;
  } else if (type === "Future") {
    symbol = t.fut_symbol;
    subtitle = t.segment;
    entry = toNum(t.entry_price);
    exit = toNum(t.exit_price);
    target = toNum(t.target_price);
    stopLoss = toNum(t.stop_loss);
    signal = t.signal;
    if (exit != null && entry) {
      const dir = (t.signal || "").toLowerCase() === "short" ? -1 : 1;
      pnlPercent = ((exit - entry) / entry) * 100 * dir;
    }
    date = t.exit_price_time || t.created_at;
  } else {
    symbol = t.trade_symbol;
    subtitle = t.script_name;
    entry = toNum(t.trade_open_price);
    exit = toNum(t.close_price);
    target = toNum(t.target_amount);
    stopLoss = toNum(t.stop_loss);
    signal = null;
    pnlPercent = toNum(t.profit_percent);
    date = t.closed_at || t.date_of_recommendation;
  }

  return {
    id: `${type}-${t.id}`,
    type,
    symbol: symbol || "—",
    subtitle,
    entry,
    exit,
    target,
    stopLoss,
    signal,
    status: statusOf(t.trade_status),
    pnlPercent,
    date,
  };
};

const statusStyle = (status) => {
  if (status === "OPEN") return { bg: "rgba(2,132,199,0.1)", color: "#0284c7" };
  if (status === "CLOSED") return { bg: "rgba(1,101,81,0.1)", color: brandGreen };
  if (status === "CANCEL" || status === "CANCELLED") return { bg: "rgba(220,38,38,0.1)", color: negRed };
  return { bg: "#f1f5f9", color: textMuted };
};

const TRADE_COLUMNS = ["Symbol", "Type", "Status", "Signal", "Entry", "Exit", "Target", "Stop Loss", "P&L", "Date"];

const TradeRow = ({ trade, index }) => {
  const st = statusStyle(trade.status);
  const hasPnl = trade.pnlPercent != null && !isNaN(trade.pnlPercent);
  const pnlPositive = hasPnl && trade.pnlPercent >= 0;

  return (
    <Box component="tr" sx={{ background: index % 2 === 0 ? "#fff" : "#f8fafc" }}>
      <Box component="td" sx={{ p: "10px 18px", borderBottom: "1px solid #f1f5f9", borderLeft: `3px solid ${typeColor(trade.type)}` }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: textMain, whiteSpace: "nowrap" }}>{trade.symbol}</Typography>
        {trade.subtitle && <Typography sx={{ fontSize: 10.5, color: textMuted }}>{trade.subtitle}</Typography>}
      </Box>
      <Box component="td" sx={{ p: "10px 18px", borderBottom: "1px solid #f1f5f9" }}>
        <TypeChip type={trade.type} />
      </Box>
      <Box component="td" sx={{ p: "10px 18px", borderBottom: "1px solid #f1f5f9" }}>
        <Box component="span" sx={{ fontSize: 10, fontWeight: 700, color: st.color, background: st.bg, borderRadius: "999px", padding: "3px 9px", whiteSpace: "nowrap" }}>
          {trade.status}
        </Box>
      </Box>
      <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMain, borderBottom: "1px solid #f1f5f9", textTransform: "capitalize", whiteSpace: "nowrap" }}>
        {trade.signal || "—"}
      </Box>
      <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
        {trade.entry != null ? fmtRs(trade.entry) : "—"}
      </Box>
      <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
        {trade.exit != null ? fmtRs(trade.exit) : "—"}
      </Box>
      <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMuted, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
        {trade.target != null ? fmtRs(trade.target) : "—"}
      </Box>
      <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMuted, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
        {trade.stopLoss != null ? fmtRs(trade.stopLoss) : "—"}
      </Box>
      <Box component="td" sx={{ p: "10px 18px", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
        {hasPnl ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            {pnlPositive ? (
              <ArrowUpwardIcon sx={{ fontSize: 14, color: brandGreen }} />
            ) : (
              <ArrowDownwardIcon sx={{ fontSize: 14, color: negRed }} />
            )}
            <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: pnlPositive ? brandGreen : negRed, fontFamily: "monospace" }}>
              {Math.abs(trade.pnlPercent).toFixed(2)}%
            </Typography>
          </Box>
        ) : (
          <Typography sx={{ fontSize: 12.5, color: textMuted }}>—</Typography>
        )}
      </Box>
      <Box component="td" sx={{ p: "10px 18px", fontSize: 12, color: textMuted, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
        {trade.date ? new Date(trade.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" }) : "—"}
      </Box>
    </Box>
  );
};

const AdvisorPerformanceDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [trades, setTrades] = useState([]);
  const [tradeType, setTradeType] = useState("All");

  const [advisors, setAdvisors] = useState([]);
  const [advisorsLoading, setAdvisorsLoading] = useState(false);
  const [selectedAdvisor, setSelectedAdvisor] = useState(null);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const loadData = async (filters = {}) => {
    setLoading(true);
    try {
      const params = {};
      if (filters.user_id) params.user_id = filters.user_id;
      if (filters.start_date) params.start_date = filters.start_date;
      if (filters.end_date) params.end_date = filters.end_date;

      const res = await axiosConfig({
        method: "get",
        url: "/tnibroadmin/performance",
        params,
      });
      setRows(Array.isArray(res.data?.response) ? res.data.response : []);
      setTrades(Array.isArray(res.data?.trades) ? res.data.trades : []);
    } catch (error) {
      console.log("advisor-performance error: ", error);
      toast.error(error?.response?.data?.response || "Failed to load advisor performance");
    } finally {
      setLoading(false);
    }
  };

  const loadAdvisors = async () => {
    setAdvisorsLoading(true);
    try {
      const res = await axiosConfig({
        method: "get",
        url: "/tnibroadmin/advisors-list",
      });
      setAdvisors(Array.isArray(res.data?.response) ? res.data.response : []);
    } catch (error) {
      console.log("advisors-list error: ", error);
      toast.error(error?.response?.data?.response || "Failed to load advisors list");
    } finally {
      setAdvisorsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    loadAdvisors();
  }, []);

  const currentFilters = () => ({
    user_id: selectedAdvisor?.id,
    start_date: fromDate,
    end_date: toDate,
  });

  const handleApply = () => loadData(currentFilters());

  const handleReset = () => {
    setSelectedAdvisor(null);
    setFromDate("");
    setToDate("");
    setTradeType("All");
    loadData({});
  };

  const tradeTypes = useMemo(
    () => ["All", ...Array.from(new Set(rows.map((r) => r.trade_type))).sort()],
    [rows]
  );

  const filteredRows = useMemo(() => {
    return rows
      .filter((r) => tradeType === "All" || r.trade_type === tradeType)
      .sort((a, b) =>
        a.advisor_name === b.advisor_name
          ? a.trade_type.localeCompare(b.trade_type)
          : a.advisor_name.localeCompare(b.advisor_name)
      );
  }, [rows, tradeType]);

  const summary = useMemo(() => {
    const advisorCount = new Set(rows.map((r) => r.advisor_id)).size;
    const totalPosted = rows.reduce((s, r) => s + num(r.total_trade_posted), 0);
    const totalProfitable = rows.reduce((s, r) => s + num(r.total_profitable_trade), 0);
    const totalOpen = rows.reduce((s, r) => s + num(r.open_trades), 0);
    const overallWinRate = totalPosted ? (totalProfitable / totalPosted) * 100 : 0;
    return { advisorCount, totalPosted, totalOpen, overallWinRate };
  }, [rows]);

  const flatTrades = useMemo(() => {
    const list = [];
    trades.forEach((group) => {
      Object.entries(TRADE_GROUP_KEYS).forEach(([key, label]) => {
        const arr = Array.isArray(group?.[key]) ? group[key] : [];
        arr.forEach((t) => list.push(normalizeTrade(label, t)));
      });
    });
    return list.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
  }, [trades]);

  const filteredTrades = useMemo(
    () => flatTrades.filter((t) => tradeType === "All" || t.type === tradeType),
    [flatTrades, tradeType]
  );

  const columns = [
    "Advisor",
    "Trade Type",
    "Posted",
    "Profitable",
    "Loss",
    "Open",
    "Win Rate",
    "Best Trade",
    "Avg Duration",
  ];

  return (
    <Box sx={{ p: 3, background: pageBg, minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          background: navy,
          borderRadius: "12px",
          boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
          p: "20px 24px",
          mb: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 44, height: 44, borderRadius: "10px", background: "rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <EmojiEventsIcon sx={{ color: "#1db954", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 20, lineHeight: 1.2 }}>
              Advisor Performance
            </Typography>
            <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: 12, mt: "2px" }}>
              Trade-wise performance breakdown for every advisor
            </Typography>
          </Box>
        </Box>

        <Box
          component="button"
          onClick={handleApply}
          sx={{
            display: "flex", alignItems: "center", gap: 0.75,
            background: brandGreen, color: "#fff", border: "none", borderRadius: "8px",
            padding: "9px 18px", fontSize: 13, fontWeight: 700, cursor: "pointer",
            boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
            "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)", boxShadow: "0 6px 16px rgba(1,101,81,0.4)" },
          }}
        >
          <RefreshIcon sx={{ fontSize: 16 }} /> Refresh
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: brandGreen }} />
        </Box>
      ) : (
        <>
          {/* Summary cards */}
          <Box sx={{ display: "flex", gap: 2.5, mb: 3, flexWrap: "wrap" }}>
            <StatCard icon={<GroupsIcon />} iconColor={navy} value={fmtInt(summary.advisorCount)} label="Advisors" />
            <StatCard icon={<BarChartIcon />} iconColor="#0284c7" value={fmtInt(summary.totalPosted)} label="Total Trades Posted" />
            <StatCard icon={<CheckCircleIcon />} iconColor={brandGreen} value={`${summary.overallWinRate.toFixed(2)}%`} label="Overall Win Rate" />
            <StatCard icon={<GpsFixedIcon />} iconColor={amber} value={fmtInt(summary.totalOpen)} label="Open Trades" />
          </Box>

          {/* Filters */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
            <Autocomplete
              size="small"
              loading={advisorsLoading}
              options={advisors}
              value={selectedAdvisor}
              onChange={(e, value) => setSelectedAdvisor(value)}
              getOptionLabel={(option) => option?.username ? `${option.username} (${option.id})` : ""}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              sx={{ background: cardBg, borderRadius: "8px", minWidth: 280 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Search advisor"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: textMuted }} />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
            />
            <TextField
              type="date"
              size="small"
              label="From"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ background: cardBg, borderRadius: "8px", minWidth: 160 }}
            />
            <TextField
              type="date"
              size="small"
              label="To"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ background: cardBg, borderRadius: "8px", minWidth: 160 }}
            />
            <Box
              component="button"
              onClick={handleApply}
              sx={{
                background: brandGreen, color: "#fff", border: "none", borderRadius: "8px",
                padding: "9px 20px", fontSize: 13, fontWeight: 700, cursor: "pointer",
                boxShadow: "0 4px 12px rgba(1,101,81,0.3)",
                "&:hover": { background: "linear-gradient(135deg, #1db954 0%, #17a349 100%)", boxShadow: "0 6px 16px rgba(1,101,81,0.4)" },
              }}
            >
              Apply
            </Box>
            <Box
              component="button"
              onClick={handleReset}
              sx={{
                display: "flex", alignItems: "center", gap: 0.75,
                background: cardBg, color: textMain, border: `1px solid ${cardBorder}`, borderRadius: "8px",
                padding: "9px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer",
                "&:hover": { background: "#f1f5f9" },
              }}
            >
              <RefreshIcon sx={{ fontSize: 16 }} /> Reset
            </Box>
          </Box>

          {/* Trade type tabs */}
          <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "12px 12px 0 0", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", borderBottom: "none" }}>
            <Tabs
              value={tradeType}
              onChange={(e, val) => setTradeType(val)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 44,
                px: 1,
                "& .MuiTabs-indicator": { backgroundColor: brandGreen, height: 3 },
              }}
            >
              {tradeTypes.map((t) => (
                <Tab
                  key={t}
                  value={t}
                  label={t}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: 13,
                    minHeight: 44,
                    color: textMuted,
                    "&.Mui-selected": { color: t === "All" ? navy : typeColor(t) },
                  }}
                />
              ))}
            </Tabs>
          </Box>

          {/* Table */}
          <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: "0 0 12px 12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            <Box sx={{ overflowX: "auto" }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 900 }}>
                <Box component="thead">
                  <Box component="tr">
                    {columns.map((h) => (
                      <Box
                        component="th"
                        key={h}
                        sx={{
                          textAlign: "left", fontSize: 11, color: "#fff", fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: "0.5px",
                          background: navy, p: "12px 18px", whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {filteredRows.length === 0 ? (
                    <Box component="tr">
                      <Box component="td" colSpan={columns.length} sx={{ p: 4, textAlign: "center", fontSize: 13, color: textMuted }}>
                        No performance data found
                      </Box>
                    </Box>
                  ) : (
                    filteredRows.map((r, i) => {
                      const newAdvisorGroup = i === 0 || filteredRows[i - 1].advisor_id !== r.advisor_id;
                      return (
                        <Box
                          component="tr"
                          key={`${r.advisor_id}-${r.trade_type}-${i}`}
                          sx={{
                            background: i % 2 === 0 ? "#fff" : "#f8fafc",
                            borderTop: newAdvisorGroup ? `2px solid ${cardBorder}` : "none",
                          }}
                        >
                          <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>
                            {newAdvisorGroup && (
                              <>
                                <Typography sx={{ fontSize: 13, fontWeight: 700, color: textMain }}>{r.advisor_name}</Typography>
                                <Typography sx={{ fontSize: 11, color: textMuted }}>ID: {r.advisor_id}</Typography>
                              </>
                            )}
                          </Box>
                          <Box component="td" sx={{ p: "10px 18px", borderBottom: "1px solid #f1f5f9" }}>
                            <TypeChip type={r.trade_type} />
                          </Box>
                          <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9" }}>{fmtInt(r.total_trade_posted)}</Box>
                          <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: brandGreen, fontWeight: 700, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9" }}>{fmtInt(r.total_profitable_trade)}</Box>
                          <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: negRed, fontWeight: 700, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9" }}>{fmtInt(r.total_loss_trades)}</Box>
                          <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9" }}>{fmtInt(r.open_trades)}</Box>
                          <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, fontWeight: 700, color: winRateColor(r.win_rate), fontFamily: "monospace", borderBottom: "1px solid #f1f5f9" }}>{fmtPct(r.win_rate)}</Box>
                          <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMain, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtRs(r.best_trade)}</Box>
                          <Box component="td" sx={{ p: "10px 18px", fontSize: 12.5, color: textMuted, fontFamily: "monospace", borderBottom: "1px solid #f1f5f9", whiteSpace: "nowrap" }}>{fmtInt(r.duration)} days</Box>
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Recent trades feed */}
          <Box
            sx={{
              background: navy,
              borderRadius: "12px 12px 0 0",
              boxShadow: "0 4px 20px rgba(4,52,58,0.3)",
              p: "16px 24px",
              mt: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>Recent Trades</Typography>
            <Box component="span" sx={{ fontSize: 11.5, fontWeight: 700, color: navy, background: "#fff", borderRadius: "999px", padding: "3px 12px" }}>
              {filteredTrades.length} trade{filteredTrades.length === 1 ? "" : "s"}
            </Box>
          </Box>
          <Box sx={{ background: cardBg, border: `1px solid ${cardBorder}`, borderTop: "none", borderRadius: "0 0 12px 12px", boxShadow: "0 1px 6px rgba(0,0,0,0.07)", overflow: "hidden" }}>
            <Box sx={{ overflowX: "auto" }}>
              <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
                <Box component="thead">
                  <Box component="tr">
                    {TRADE_COLUMNS.map((h) => (
                      <Box
                        component="th"
                        key={h}
                        sx={{
                          textAlign: "left", fontSize: 11, color: "#fff", fontWeight: 700,
                          textTransform: "uppercase", letterSpacing: "0.5px",
                          background: navy, p: "12px 18px", whiteSpace: "nowrap",
                        }}
                      >
                        {h}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box component="tbody">
                  {filteredTrades.length === 0 ? (
                    <Box component="tr">
                      <Box component="td" colSpan={TRADE_COLUMNS.length} sx={{ p: 4, textAlign: "center", fontSize: 13, color: textMuted }}>
                        No trades found
                      </Box>
                    </Box>
                  ) : (
                    filteredTrades.map((t, i) => <TradeRow key={t.id} trade={t} index={i} />)
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
        </>
      )}
    </Box>
  );
};

export default AdvisorPerformanceDashboard;
